import React, { useEffect, useMemo, useState } from "react";
import { Button, Popconfirm, Space, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import MomentDatePicker from "../../MomentDatePicker";
import moment from "moment";
import { formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";

// Editable date field for organizzazione.subscription_started_at
const FieldSubscriptionStartedAt = ({ org }) => {
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();

  const [editing, setEditing] = useState(false);
  const initVal = (() => {
    try {
      const iso = org?.subscription_started_at?.iso || org?.subscription_started_at;
      return iso ? moment(iso) : null;
    } catch (_) {
      return null;
    }
  })();
  const [value, setValue] = useState(initVal);
  const [originalValue, setOriginalValue] = useState(initVal);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const displayValue = useMemo(() => {
    if (!value) return "-";
    try {
      const dateStr = new Intl.DateTimeFormat("it-IT").format(value.toDate());
      const relative = formatDistanceToNow(value.toDate(), { addSuffix: true, locale: it });
      return `${dateStr} (${relative})`;
    } catch (_) {
      return String(value);
    }
  }, [value]);

  const handleConfirm = async () => {
    if (!value) { setConfirmOpen(false); setEditing(false); return; }
    try {
      setSaving(true);
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[isProd ? "prod" : "dev"]}/graphql?raw=true`;
      const mutation = `
        mutation UpdateOrganizzazione($id: ID!, $fields: UpdateOrganizzazioneFieldsInput) {
          updateOrganizzazione(input: { id: $id, fields: $fields }) {
            organizzazione { objectId subscription_started_at }
          }
        }
      `.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

      const body = {
        query: mutation,
        variables: { id: org.objectId, fields: { subscription_started_at: value.toDate().toISOString() } },
      };

      const resp = await fetch(graphqlUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const json = await resp.json();
      if (json.errors) throw new Error(json.errors[0]?.message || "GraphQL error");

      message.success("Fatto");
      // Persist new original value so future cancels restore this
      setOriginalValue(value);
      setConfirmOpen(false);
      setEditing(false);
    } catch (e) {
      console.error("Errore aggiornamento subscription_started_at:", e);
      message.error("Si è verificato un problema: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelAll = () => {
    // Restore original value if user cancels confirmation
    setValue(originalValue);
    setConfirmOpen(false);
    setEditing(false);
  };

  // If org prop changes (e.g., refreshed), sync original/value
  useEffect(() => {
    const iso = org?.subscription_started_at?.iso || org?.subscription_started_at;
    const v = iso ? moment(iso) : null;
    setValue(v);
    setOriginalValue(v);
  }, [org?.subscription_started_at]);

  return (
    <>
      {!editing && (
        <Space size="small">
          <span>{displayValue}</span>
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => setEditing(true)}>
            Modifica
          </Button>
        </Space>
      )}
      {editing && (
        <Space size="small">
          <Popconfirm
            title="Sei sicuro di voler aggiornare la org?"
            open={confirmOpen}
            okButtonProps={{ loading: saving }}
            onConfirm={handleConfirm}
            onCancel={() => { setConfirmOpen(false); setEditing(false); }}
          >
            <MomentDatePicker
              onChange={(m) => { if (!m) return; setValue(m); setConfirmOpen(true); }}
              value={value}
            />
          </Popconfirm>
          <Button size="small" onClick={handleCancelAll}>Annulla</Button>
        </Space>
      )}
    </>
  );
};

export default FieldSubscriptionStartedAt;
